import type { UbaProduct } from '../types'
import type { SetStateAction } from 'react'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { HiXMark } from 'react-icons/hi2'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import { NoDataPlaceHolder } from '../../PatientFolder/behavioral/components'
import { CircularProgress } from '../../shared/NarusMaterial'

import { ProductCard } from './product-card'


function useProducts ({ query }: { query: string }) {
  return useQuery({
    queryKey: [ 'uba-products', query ],
    queryFn: async ({ queryKey }) => {
      const [ , search ] = queryKey

      if (!search?.length) {
        return []
      }

      const { data } = await http.postRequest<{ data: UbaProduct[] }>('/uba/getproducts', { productName: search })

      return data
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData
  })
}

interface Props {
  products: Array<UbaProduct & { quantity: number }>
  setProducts: React.Dispatch<SetStateAction<Array<UbaProduct & { quantity: number }>>>
}

export function CreateOrderForm ({ products, setProducts }: Props): JSX.Element {
  const [ query, setQuery ] = useState('')
  const [ debouncedQuery ] = useDebounceValue(query, 600)
  const { data: productsData, isSuccess, isFetching, isLoading } = useProducts({ query: debouncedQuery })

  function handleSelect (productId: number): void {
    const product = productsData?.find((p) => p.productID === productId)

    if (!product) {
      return
    }

    setProducts((prev) => [
      ...prev,
      { ...product, quantity: product.minimumOrderQty }
    ])
  }

  function handleChangeQuantity (productId: number, dir: 'less' | 'more'): void {
    const productIndex = products?.findIndex((p) => p.productID === productId)

    if (productIndex < 0) {
      return
    }

    setProducts((prev) => {
      const item = prev[productIndex]

      if (!item) {
        return prev
      }

      const nextQuantity = (item.quantity ?? 0) + (dir === 'less' ? -1 : 1)

      if (nextQuantity <= 0) {
        return prev
      }

      return [
        ...prev.slice(0, productIndex),
        { ...item, quantity: nextQuantity },
        ...prev.slice(productIndex + 1, prev.length)
      ]
    })
  }

  function handleRemove (id: number): void {
    setProducts((prev) => prev.filter((product) => product.productID !== id))
  }

  return (
    <div className='h-full'>
      <div className='p-8'>
        {products.length > 0 && (
          <>
            <h3>Your Order</h3>

            <div className='flex flex-col gap-4'>
              {products.map((p) => (
                <ProductCard
                  key={`selected ${p.productID}`}
                  product={p}
                  handleSelect={handleSelect}
                  handleChangeQuantity={handleChangeQuantity}
                  selected={p}
                  handleRemove={handleRemove}
                />
              ))}
            </div>
            <div className='p-8 px-12'>
              <hr className='bg-zinc-200 w-full h-[1px] box-border' />
            </div>
          </>
        )}

        <div className='relative'>
          <input
            type='text'
            className='px-4 py-3 bg-zinc-100 w-full box-border rounded-md'
            placeholder='Type name of medication'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {isFetching && (
            <div className='absolute top-1/2 right-3 -translate-y-1/2'>
              <CircularProgress className='w-5 h-5' />
            </div>
          )}

          {!isFetching && !!query.length && (
            <button
              className='cursor-pointer absolute top-1/2 right-3 -translate-y-1/2'
              onClick={() => setQuery('')}
            >
              <HiXMark size='1.2rem' />
            </button>
          )}
        </div>

        {!productsData?.length && isSuccess && isFetching && !!query.length && (
          <div className='mt-8'>
            <NoDataPlaceHolder title='Searching for medication' />
          </div>
        )}

        {!productsData?.length && isSuccess && !isFetching && !isLoading && !!query.length && (
          <div className='mt-8'>
            <NoDataPlaceHolder title={'We can\'t find any medication with this name'} />
          </div>
        )}

        {!productsData?.length && !query.length && (
          <div className='mt-8'>
            <NoDataPlaceHolder title='Start typing medication name to find a medication' />
          </div>
        )}

        <div className='flex flex-col gap-4 mt-4'>
          {productsData?.map((product) => {
            const selected = products.find((p) => p.productID === product.productID)
            return (
              <ProductCard
                key={product.productID}
                product={product}
                selected={selected}
                handleSelect={handleSelect}
                handleChangeQuantity={handleChangeQuantity}
                handleRemove={handleRemove}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
